import React, { useState, useRef, useEffect } from "react";
import {
  Mail,
  MessageCircle,
  Instagram,
  Linkedin,
  Send,
  MapPin,
  Phone,
  Clock,
  CheckCircle,
  Loader,
  User,
  AtSign,
  MessageSquare
} from "lucide-react";

const Contacto = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });
  const [enviado, setEnviado] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [errors, setErrors] = useState({});
  const [focusedField, setFocusedField] = useState(null);
  
  const canvasRef = useRef(null);

  // Animación de partículas en el fondo
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    
    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    const particles = [];
    for (let i = 0; i < 50; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        dx: (Math.random() - 0.5) * 0.5,
        dy: (Math.random() - 0.5) * 0.5,
        size: Math.random() * 2 + 1,
        opacity: Math.random() * 0.5 + 0.2
      });
    }
    
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      particles.forEach(particle => {
        particle.x += particle.dx;
        particle.y += particle.dy;
        
        if (particle.x < 0 || particle.x > canvas.width) particle.dx = -particle.dx;
        if (particle.y < 0 || particle.y > canvas.height) particle.dy = -particle.dy;
        
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(29, 233, 182, ${particle.opacity * 0.6})`;
        ctx.fill();
      });
      
      // Líneas de conexión
      particles.forEach((particle, i) => {
        particles.slice(i + 1).forEach(otherParticle => {
          const dx = particle.x - otherParticle.x;
          const dy = particle.y - otherParticle.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < 100) {
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(otherParticle.x, otherParticle.y);
            ctx.strokeStyle = `rgba(29, 233, 182, ${0.05 * (1 - distance / 100)})`;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        });
      });
      
      animationFrameId = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  const validateField = (name, value) => {
    const newErrors = { ...errors };
    
    switch (name) {
      case 'name':
        if (!value.trim()) {
          newErrors.name = 'El nombre es requerido';
        } else if (value.trim().length < 2) {
          newErrors.name = 'El nombre debe tener al menos 2 caracteres';
        } else {
          delete newErrors.name;
        }
        break;
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!value.trim()) {
          newErrors.email = 'El email es requerido';
        } else if (!emailRegex.test(value)) {
          newErrors.email = 'Email inválido';
        } else {
          delete newErrors.email;
        }
        break;
      case 'message':
        if (!value.trim()) {
          newErrors.message = 'El mensaje es requerido';
        } else if (value.trim().length < 10) {
          newErrors.message = 'El mensaje debe tener al menos 10 caracteres';
        } else {
          delete newErrors.message;
        }
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    validateField(name, value);
  };

  const handleSubmit = async () => {
    // Validar todos los campos
    const isValid = Object.keys(formData).every(key => 
      validateField(key, formData[key])
    );
    
    if (!isValid) return;
    
    setEnviando(true);
    
    try {
      const data = new URLSearchParams();
      data.append('name', formData.name);
      data.append('email', formData.email);
      data.append('message', formData.message);
      
      const response = await fetch("https://formspree.io/f/mblozapl", {
        method: "POST",
        body: data,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: "application/json",
        },
      });
      
      if (response.ok) {
        setEnviado(true);
        setFormData({ name: "", email: "", message: "" });
        setTimeout(() => setEnviado(false), 5000);
      } else {
        throw new Error('Error al enviar');
      }
    } catch (error) {
      alert("Hubo un error al enviar. Intenta de nuevo.");
    } finally {
      setEnviando(false);
    }
  };

  const contactInfo = [
    {
      icon: MessageCircle,
      title: "WhatsApp",
      value: "+56 9 8824 7608",
      link: "https://wa.me/56988247608?text=Hola!%20Estoy%20interesado%20en%20los%20servicios%20de%20Zennith",
      color: "text-green-400"
    },
    {
      icon: Mail,
      title: "Email",
      value: "contacto@zennith.cl",
      link: "mailto:contacto@zennith.com",
      color: "text-blue-400"
    },
    {
      icon: MapPin,
      title: "Ubicación",
      value: "Santiago, Chile",
      color: "text-red-400"
    },
    {
      icon: Clock,
      title: "Horario",
      value: "Lun - Vie: 9:00 - 18:00",
      color: "text-yellow-400"
    }
  ];

  const socialLinks = [
    {
      icon: Instagram,
      url: "#",
      color: "hover:text-pink-400",
      label: "Instagram"
    },
    {
      icon: Linkedin,
      url: "#",
      color: "hover:text-blue-500",
      label: "LinkedIn"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-black to-gray-900 relative overflow-hidden">
      {/* Canvas de partículas */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none opacity-20"
      />
      
      {/* Efectos de fondo */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#1de9b6] opacity-3 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-cyan-400 opacity-3 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-20">
        <div className="w-full max-w-6xl">
          {/* Toast de éxito */}
          {enviado && (
            <div className="fixed top-6 right-6 z-50 animate-in slide-in-from-right duration-300">
              <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-4 rounded-lg shadow-2xl flex items-center gap-3">
                <CheckCircle className="w-6 h-6" />
                <div>
                  <p className="font-semibold">¡Mensaje enviado!</p>
                  <p className="text-sm opacity-90">Te responderemos pronto</p>
                </div>
              </div>
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Información de contacto */}
            <div className="space-y-8">
              <div className="text-center lg:text-left">
                <h1 className="text-5xl lg:text-6xl font-bold bg-gradient-to-r from-[#1de9b6] to-cyan-400 bg-clip-text text-transparent mb-4">
                  Contáctanos
                </h1>
                <p className="text-xl text-gray-300 leading-relaxed">
                  Estamos aquí para ayudarte a hacer realidad tus proyectos digitales. 
                  Conversemos sobre cómo podemos impulsar tu negocio.
                </p>
              </div>

              {/* Tarjetas de información */}
              <div className="grid sm:grid-cols-2 gap-6">
                {contactInfo.map((item, index) => (
                  <div
                    key={index}
                    className="group p-6 bg-black/30 backdrop-blur-sm rounded-2xl border border-gray-800 hover:border-[#1de9b6]/50 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-[#1de9b6]/10"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-xl bg-gradient-to-br from-gray-900 to-black group-hover:from-[#1de9b6]/10 group-hover:to-cyan-400/10 transition-all duration-300`}>
                        <item.icon className={`w-6 h-6 ${item.color} group-hover:text-[#1de9b6] transition-colors duration-300`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-white group-hover:text-[#1de9b6] transition-colors">
                          {item.title}
                        </h3>
                        {item.link ? (
                          <a 
                            href={item.link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-gray-400 hover:text-[#1de9b6] transition-colors text-sm"
                          >
                            {item.value}
                          </a>
                        ) : (
                          <p className="text-gray-400 text-sm">{item.value}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Redes sociales */}
              <div className="text-center lg:text-left">
                <h3 className="text-2xl font-semibold text-white mb-6">Síguenos</h3>
                <div className="flex justify-center lg:justify-start gap-4">
                  {socialLinks.map((social, index) => (
                    <a
                      key={index}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`group p-4 bg-black/30 backdrop-blur-sm rounded-2xl border border-gray-800 hover:border-[#1de9b6]/50 transition-all duration-300 hover:scale-110 hover:shadow-lg ${social.color}`}
                      aria-label={social.label}
                    >
                      <social.icon className="w-6 h-6 text-gray-400 group-hover:text-current transition-colors duration-300" />
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Formulario */}
            <div className="bg-black/40 backdrop-blur-lg rounded-3xl border border-gray-800 p-8 shadow-2xl">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-white mb-2">Envíanos un mensaje</h2>
                <p className="text-gray-400">Completa el formulario y te responderemos pronto</p>
              </div>

              <div className="space-y-6">
                {/* Campo Nombre */}
                <div className="relative group">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Nombre completo
                  </label>
                  <div className="relative">
                    <User className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 transition-colors duration-300 ${
                      focusedField === 'name' ? 'text-[#1de9b6]' : 'text-gray-500'
                    }`} />
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      onFocus={() => setFocusedField('name')}
                      onBlur={() => setFocusedField(null)}
                      placeholder="Tu nombre completo"
                      required
                      className={`w-full pl-12 pr-4 py-4 bg-black/20 border rounded-xl placeholder-gray-600 text-white focus:outline-none focus:ring-2 transition-all duration-300 ${
                        errors.name 
                          ? 'border-red-500 focus:ring-red-500/50' 
                          : focusedField === 'name'
                          ? 'border-[#1de9b6] focus:ring-[#1de9b6]/50'
                          : 'border-gray-700 hover:border-gray-600'
                      }`}
                    />
                  </div>
                  {errors.name && <p className="text-red-400 text-sm mt-2">{errors.name}</p>}
                </div>

                {/* Campo Email */}
                <div className="relative group">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Correo electrónico
                  </label>
                  <div className="relative">
                    <AtSign className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 transition-colors duration-300 ${
                      focusedField === 'email' ? 'text-[#1de9b6]' : 'text-gray-500'
                    }`} />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      onFocus={() => setFocusedField('email')}
                      onBlur={() => setFocusedField(null)}
                      placeholder="tu@email.com"
                      required
                      className={`w-full pl-12 pr-4 py-4 bg-black/20 border rounded-xl placeholder-gray-600 text-white focus:outline-none focus:ring-2 transition-all duration-300 ${
                        errors.email 
                          ? 'border-red-500 focus:ring-red-500/50' 
                          : focusedField === 'email'
                          ? 'border-[#1de9b6] focus:ring-[#1de9b6]/50'
                          : 'border-gray-700 hover:border-gray-600'
                      }`}
                    />
                  </div>
                  {errors.email && <p className="text-red-400 text-sm mt-2">{errors.email}</p>}
                </div>

                {/* Campo Mensaje */}
                <div className="relative group">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Mensaje
                  </label>
                  <div className="relative">
                    <MessageSquare className={`absolute left-4 top-4 w-5 h-5 transition-colors duration-300 ${
                      focusedField === 'message' ? 'text-[#1de9b6]' : 'text-gray-500'
                    }`} />
                    <textarea
                      name="message"
                      rows="5"
                      value={formData.message}
                      onChange={handleInputChange}
                      onFocus={() => setFocusedField('message')}
                      onBlur={() => setFocusedField(null)}
                      placeholder="Cuéntanos sobre tu proyecto..."
                      required
                      className={`w-full pl-12 pr-4 py-4 bg-black/20 border rounded-xl placeholder-gray-600 text-white focus:outline-none focus:ring-2 transition-all duration-300 resize-none ${
                        errors.message 
                          ? 'border-red-500 focus:ring-red-500/50' 
                          : focusedField === 'message'
                          ? 'border-[#1de9b6] focus:ring-[#1de9b6]/50'
                          : 'border-gray-700 hover:border-gray-600'
                      }`}
                    />
                  </div>
                  {errors.message && <p className="text-red-400 text-sm mt-2">{errors.message}</p>}
                </div>

                {/* Botón de envío */}
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={enviando || Object.keys(errors).length > 0}
                  className="w-full group relative overflow-hidden bg-gradient-to-r from-[#1de9b6] to-cyan-400 text-black font-semibold py-4 px-6 rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-[#1de9b6]/25 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-[#1de9b6] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative flex items-center justify-center gap-3">
                    {enviando ? (
                      <>
                        <Loader className="w-5 h-5 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                        Enviar mensaje
                      </>
                    )}
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contacto;